import { Sponsorstripe } from '@schibsted/advertory/aftenposten/cjs';
import React, { useEffect, useRef } from 'react';

import type { ListPageProps } from './index.js';

import { useSessionData } from '../../../../../public-src/core/js/schibsted-account/react.server.js';
import {
  LinkWithPulse,
  useByfunnPulseContext,
  buildListPageView,
} from '../../../pulse/index.js';
import Ad from '../../components/ads/Ad/Ad.js';
import Footer from '../../components/commonComponents/Footer.js';
import Navigation from '../../components/commonComponents/Navigation/Navigation.js';
import { ListsProvider } from '../../components/saving/ListsProvider.js';
import Teaser from '../../components/teasers/Teaser.js';
import TeaserList from '../../components/teasers/TeaserList.js';
import useInitializeAds from '../../hooks/useInitAds.js';
import RestaurantsWidget from '../front/RestaurantsWidget.js';
import { buildFaq } from './buildFaq.js';
import { ListDescription } from './ListDescription.js';
import ListPageFaq from './ListPageFaq.js';
import { insertAds } from './utils/ads.js';
import { getRelatedTopLists } from './utils/related-top-list.js';

const ListPageV1: React.FC<ListPageProps & { isTopList: boolean }> = ({
  data,
  options,
  isTopList,
}) => {
  const { siteVersion, currentUrl } = options;
  const { properties, items } = data;
  const { faq } = properties;

  const isMobile = siteVersion === 'phone';

  const ref = useRef<HTMLDivElement>(null);

  const { trackViewListing, getEnvironmentId } = useByfunnPulseContext();

  const session = useSessionData();

  useInitializeAds(getEnvironmentId, session, siteVersion);

  useEffect(() => {
    const { legacy, tpaas } = buildListPageView({
      currentUrl,
      title: properties.h1,
      itemsTotal: items?.length ?? 0,
      topListVersion: isTopList ? 1 : undefined,
    });

    trackViewListing(legacy, tpaas);
  }, [trackViewListing, currentUrl, properties.h1, items?.length, isTopList]);

  const builtFaqs = faq && buildFaq(faq);

  const relatedTopLists = getRelatedTopLists(data.properties);

  const itemsWithAds = items ? insertAds(items, isMobile) : [];

  return (
    <ListsProvider>
      <div ref={ref} className={`${siteVersion}-front`}>
        <Navigation fullWidth={!isMobile} activeSection="restaurantguiden" />
        <Sponsorstripe targetId="sponsorstripe" />
        <div className="tag-page-container">
          <div className="title-description">
            <h1 className="story-title">{properties.h1}</h1>
            <ListDescription
              className="description"
              text={properties.description}
            />
          </div>
          <div className="story-controls">
            <div className="story-hits">{`${items?.length} ${isTopList ? 'anmeldelser' : 'lister'}`}</div>
          </div>
          <div className="tag-articles-all">
            {(() => {
              let position = 0;

              return itemsWithAds?.map((item) => {
                if (item.type === 'ad') {
                  return (
                    <Ad
                      key={`Ad-${item.id}`}
                      id={item.id}
                      cssClass={item.cssClass}
                      isMobile={isMobile}
                      containerClass="ad-list"
                    />
                  );
                }

                position += 1;

                if (item.type === 'teaser') {
                  return (
                    <Teaser
                      {...item}
                      key={item.id}
                      size={isMobile ? 'big' : 'medium'}
                      mobile={isMobile}
                      fullWidthTeaser={false}
                      lazyLoad={false}
                      colorPalette={undefined}
                      hasGroups={false}
                      pulseData={{
                        contentSource: 'Editorial',
                        placement: {
                          primaryPosition: position,
                        },
                      }}
                    />
                  );
                }

                return (
                  <TeaserList
                    {...item}
                    key={item.id}
                    mobile={isMobile}
                    size="medium"
                    slug=""
                    pulseData={{
                      contentSource: 'Editorial',
                      placement: {
                        primaryPosition: position,
                      },
                      content: {
                        format: 'Other',
                        formatSubtype: 'Unknown',
                      },
                    }}
                  />
                );
              });
            })()}
          </div>
          {relatedTopLists.length > 0 && (
            <RestaurantsWidget
              data={{
                title: 'Topplister fra våre anmeldere',
                items: relatedTopLists,
                properties,
              }}
              isMobile={isMobile}
            />
          )}
          {data.properties.faq && builtFaqs && (
            <ListPageFaq
              faqs={builtFaqs}
              listId={options.requestParams.childId}
            />
          )}
          <LinkWithPulse
            className="homescreen-button-centered"
            href="/"
            objectId="redirect-to-homescreen"
            name="Redirect to homescreen"
          >
            Gå til Vink-forsiden
          </LinkWithPulse>
        </div>
        <Footer isMobile={isMobile} />
      </div>
    </ListsProvider>
  );
};

export { ListPageV1 };
